/**
 * Represents a timespan
 * totalMilliseconds is the total number of milliseconds in the timespan from 0:00:00.000
 * The value can also be negative
 */

import type { ISerialize } from './ISerialize';

export interface ITimeSpan extends ISerialize {
    readonly Days: number;

    Hours(includedays?: boolean): number;

    readonly Minutes: number;

    readonly Seconds: number;

    readonly Milliseconds: number;

    readonly TotalMilliseconds: number;

    readonly IsNegative: boolean;
}

export class TimeSpan implements ITimeSpan {
    private totalMilliseconds: number = 0;

    private days: number = 0;

    /**
     * The hours in the timespan
     */
    private hours: number = 0;

    /**
     * The minutes in the timespan
     */
    private minutes: number = 0;

    /**
     * The seconds in the timespan
     */
    private seconds: number = 0;

    /**
     * The milliseconds in the timespan
     */
    private milliseconds: number = 0;

    /**
     * Create a new timespan from total milliseconds
     * @param totalmilliseconds
     */
    private constructor(totalmilliseconds: number) {
        this.totalMilliseconds = totalmilliseconds;

        // Calculate the parts of the timespan
        this.milliseconds = Math.abs(totalmilliseconds) % 1000;
        this.seconds = Math.floor(Math.abs(totalmilliseconds) / 1000) % 60;
        this.minutes = Math.floor(Math.abs(totalmilliseconds) / (1000 * 60)) % 60;
        this.hours = Math.floor(Math.abs(totalmilliseconds) / (1000 * 60 * 60));

        // Calculate the full days and remove them from the hours
        this.days = Math.floor(this.hours / 24);
        this.hours = this.hours % 24;
    }

    /**
     * Gives a timespan that spans zero time
     * @returns A timespan that spans zero time
     */
    public static Empty() {
        return new TimeSpan(0);
    }

    /**
     * Gives a timespan that spans the given milliseconds
     * @param milliseconds The total number of milliseconds in the timespan
     * @returns A timespan that spans the specified number of milliseconds
     */
    public static FromMilliseconds(milliseconds: number): TimeSpan {
        return new TimeSpan(milliseconds);
    }

    /**
     * Give a timespan that spans the given number of seconds
     * @param seconds The total number of seconds in the timespan
     * @returns A TimeSpan that spans the specified number of seconds
     */
    public static FromSeconds(seconds: number): TimeSpan {
        return new TimeSpan(seconds * 1000);
    }

    /**
     * Create a timespan from minutes
     * @param minutes
     * @returns
     */
    public static FromMinutes(minutes: number): TimeSpan {
        return new TimeSpan(minutes * 60 * 1000);
    }

    /**
     * Create a timespan from hours
     * @param hours
     * @returns
     */
    public static FromHours(hours: number): TimeSpan {
        return new TimeSpan(hours * 60 * 60 * 1000);
    }

    /**
     * Create a timespan from days.
     * @param days
     * @returns
     */
    public static FromDays(days: number): TimeSpan {
        return new TimeSpan(days * 24 * 60 * 60 * 1000);
    }

    // /**
    //  * Create the timespan from seconds, minutes, hours and milliseconds
    //  * @param seconds
    //  * @param minutes
    //  * @param hours
    //  * @param milliseconds
    //  * @param isnegative
    //  */
    // public static FromParts(
    //     seconds: number,
    //     minutes: number,
    //     hours: number = 0,
    //     milliseconds: number = 0,
    //     isnegative: boolean = false,
    // ): TimeSpan {
    //     const totalMilliseconds =
    //         (milliseconds ?? 0) + seconds * 1000 + minutes * 60 * 1000 + (hours ?? 0) * 60 * 60 * 1000 * (isnegative ? -1 : 1);

    //     return new TimeSpan(totalMilliseconds);
    // }

    /**
     * Will parse a timestring to a timespan
     * The timestring can be in the format of hh:mm:ss, mm:ss or hh:mm:ss.mmm or mm:ss.mmm it can ALSO start with a number of days separated by a .
     * @param timestring
     * @returns
     */
    public static FromTimeString(timestring: string): TimeSpan {
        // First we sanitize the timestring
        timestring = timestring.trim();

        const firstDot = timestring.indexOf('.', 0); // We need to check if we have a dot. If there is no dot we dont have any day or decimal part

        let hasDay = false;
        let hasDecimal = false;

        if (firstDot !== -1) {
            // We need to know the last time part i.e the last position of :
            const lastColon = timestring.lastIndexOf(':');

            // We have a dot, so we need to check if there is a day part
            hasDay = timestring.indexOf('.', 0) < lastColon;
            hasDecimal = timestring.indexOf('.', lastColon) > lastColon;
        }

        // Is the value negative?
        const isNegative = timestring.indexOf('-') !== -1;

        // We grab the timepart
        if (isNegative) {
            timestring = timestring.substring(1);
        }

        // We split the dot parts
        const dotparts = timestring.split('.');

        // If we have a day we know that the time part is the second part or else it is the first part
        timestring = dotparts[hasDay ? 1 : 0];

        // we split the time part by :
        const timeparts = timestring.split(':');

        if (timeparts.length < 2) {
            throw new Error(`Invalid timestring: ${timestring}`);
        }

        // We grab the day part if there is one
        const days = hasDay ? parseInt(dotparts[0]) : 0;
        const milliseconds = hasDecimal ? parseFloat('0.' + dotparts.pop()) * 1000 : 0; // We need to set the milliseconds to 0

        // We get the different parts of the timestring.
        const seconds = parseInt(timeparts.pop() ?? '0') || 0;
        const minutes = parseInt(timeparts.pop() ?? '0') || 0;
        const hours = timeparts.length == 0 ? 0 : parseInt(timeparts.pop() ?? '0') || 0; // We need to check if we have a hours part
        const totalMilliseconds =
            (milliseconds + seconds * 1000 + minutes * 60 * 1000 + (hours + days * 24) * 60 * 60 * 1000) * (isNegative ? -1 : 1); // We need to check if we have a negative value

        return TimeSpan.FromMilliseconds(totalMilliseconds);
    }

    /**
     * Get the total milliseconds in the timespan. Can be positive or negative and is the sum of all parts
     */
    public get TotalMilliseconds(): number {
        return this.totalMilliseconds;
    }

    /**
     * Return the days part of the timespan
     */
    public get Days(): number {
        return this.days;
    }

    /**
     * Return the hour part of the timespan
     * @param includedays if true the days will be included in the hours
     */
    public Hours(includedays: boolean = false): number {
        return this.hours + (includedays ? this.days * 24 : 0);
    }

    /**
     * Return the minute part of the timespan
     */
    public get Minutes(): number {
        return this.minutes;
    }

    /**
     * Return the second part of the timespan
     */
    public get Seconds(): number {
        return this.seconds;
    }

    /**
     * Return the millisecond part of the timespan
     */
    public get Milliseconds(): number {
        return this.milliseconds;
    }

    /**
     * If the timespan is negative or not
     */
    public get IsNegative(): boolean {
        return this.totalMilliseconds < 0;
    }

    /**
     * Serialize the timespan to a string
     * @returns
     */
    public serialize(): string {
        return `${this.IsNegative ? '-' : ''}${this.days}.${this.hours.toString().padStart(2, '0')}:${this.minutes.toString().padStart(2, '0')}:${this.seconds.toString().padStart(2, '0')}.${this.milliseconds.toString().padStart(3, '0')}`;
    }
}
